import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../api/axios';
import { formatPrice } from '../../utils/plans';

const formatDate = (iso) => new Date(iso).toLocaleDateString('pl-PL');

function AdminInvoices() {
  const userInfo = useSelector(state => state.userLogin.userInfo);
  const isAdmin = !!userInfo?.isAdmin;

  const [tab, setTab] = useState('pending');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [sending, setSending] = useState({});
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/comarch/x7k2-panel/payments/');
      setPayments(Array.isArray(res.data) ? res.data : []);
      setLoadError('');
    } catch {
      setLoadError('Nie udało się pobrać płatności.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) return <Navigate to="/" replace />;

  const send = async (id) => {
    setSending(prev => ({ ...prev, [id]: true }));
    setErrors(prev => ({ ...prev, [id]: '' }));
    try {
      await api.post(`/comarch/x7k2-panel/payment/${id}/invoice/`);
      await load();
    } catch (err) {
      const data = err.response?.data || {};
      setErrors(prev => ({ ...prev, [id]: data.detail || data.error || 'Błąd połączenia z Comarch.' }));
    } finally {
      setSending(prev => ({ ...prev, [id]: false }));
    }
  };

  const pending = payments.filter(p => !p.comarch_invoice_id);
  const issued = payments.filter(p => p.comarch_invoice_id);
  const rows = tab === 'pending' ? pending : issued;

  return (
    <div className="pub pub-wrap py-5">
      <div className="pub-kicker">Panel admina</div>
      <h1 className="pub-h2">Faktury Comarch</h1>
      <p className="pub-muted">Opłacone płatności. Faktura trafia do Comarch Betterfly po kliknięciu przycisku.</p>

      <div className="pub-actions" style={{ margin: '24px 0' }}>
        <button type="button" className={tab === 'pending' ? 'pub-btn' : 'pub-link'}
          style={tab === 'pending' ? undefined : { background: 'none', border: 'none' }}
          onClick={() => setTab('pending')}>
          Do wystawienia ({pending.length})
        </button>
        <button type="button" className={tab === 'issued' ? 'pub-btn' : 'pub-link'}
          style={tab === 'issued' ? undefined : { background: 'none', border: 'none' }}
          onClick={() => setTab('issued')}>
          Wystawione ({issued.length})
        </button>
      </div>

      {loading && <p className="pub-small">Wczytywanie…</p>}
      {loadError && <p style={{ color: '#b91c1c' }}>{loadError}</p>}
      {!loading && !loadError && rows.length === 0 && (
        <p className="pub-muted">{tab === 'pending' ? 'Wszystkie faktury są wystawione.' : 'Brak wystawionych faktur.'}</p>
      )}

      <ul className="pub-list">
        {rows.map(p => (
          <li key={p.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ minWidth: 220 }}>
              <b>{`${p.user.first_name} ${p.user.last_name}`.trim() || p.user.email}</b>
              <div className="pub-small">{p.user.email}</div>
              {p.company && <div className="pub-small"><span>{p.company.name}</span> · NIP {p.company.nip}</div>}
            </div>
            <div className="pub-muted" style={{ minWidth: 160 }}>
              {p.plan.name}
              <div className="pub-small">{formatDate(p.created_at)}</div>
            </div>
            <div style={{ fontFamily: 'var(--pub-serif)', fontSize: 22 }}>{formatPrice(p.amount)}</div>
            <div style={{ minWidth: 220, textAlign: 'right' }}>
              {p.comarch_invoice_id ? (
                <span className="pub-small">Comarch #{p.comarch_invoice_id}</span>
              ) : (
                <button type="button" className="pub-btn" disabled={sending[p.id]} onClick={() => send(p.id)}>
                  {sending[p.id] ? 'Wysyłanie…' : 'Wystaw i wyślij do Comarch'}
                </button>
              )}
              {errors[p.id] && <div style={{ color: '#b91c1c', fontSize: 14, marginTop: 6 }}>{errors[p.id]}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default AdminInvoices;
